import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import {
  getPodPromoIntent,
  clearPodPromoIntent,
  getPodChallengeIntent,
  clearPodChallengeIntent,
} from '@/lib/scanProfileStatus';
import { type ActiveView, getStoredActiveView, storeActiveView } from '@/lib/activeView';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userProfile: any | null;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signOut: () => Promise<any>;
  isTrainer: boolean;
  isClient: boolean;
  /**
   * The side of the app they're using right now. Use this, not isTrainer, to
   * decide "show the coach version": someone who is both a member and a coach
   * has isTrainer true while browsing the member app.
   */
  activeView: ActiveView;
  /** Only does anything for someone who is both a member and a coach. */
  setActiveView: (view: ActiveView) => void;
  /** Re-read roles, e.g. right after a member gets a coach profile. */
  refreshRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  userProfile: null,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  isTrainer: false,
  isClient: false,
  activeView: 'member',
  setActiveView: () => {},
  refreshRoles: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [storedView, setStoredView] = useState<ActiveView>('member');

  useEffect(() => {
    let isMounted = true;

    // Check for auth tokens in URL (both hash-based and PKCE code-based)
    const hashFragment = window.location.hash;
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    
    const hasMagicLinkHash = hashFragment.includes('access_token') ||
                              hashFragment.includes('type=magiclink');
    const hasPKCECode = !!authCode;
    // token_hash is used by email verification (signup/recovery) links
    const hasTokenHash = !!urlParams.get('token_hash');
    const hasAuthToken = hasMagicLinkHash || hasPKCECode || hasTokenHash;
    
    if (hasAuthToken) {
      console.log('[Auth] Auth token detected:', hasMagicLinkHash ? 'hash-based' : 'PKCE code');
    }

    // Safety timeout - if auth hasn't processed in 8 seconds, stop loading
    let authTimeout: NodeJS.Timeout | null = null;
    if (hasAuthToken) {
      authTimeout = setTimeout(() => {
        console.warn('[Auth] Auth processing timeout');
        if (isMounted) setLoading(false);
      }, 8000);
    }

    // Function to clean URL after auth
    const cleanAuthUrl = () => {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, '', cleanUrl);
    };

    // Handle PKCE code exchange if present.
    // NOTE: On /auth/callback, AuthCallback.tsx owns the code exchange and all
    // post-OAuth routing. AuthContext must NOT touch the code on that route, or
    // the two handlers race and can consume the single-use code before AuthCallback.
    const isAuthCallbackRoute = window.location.pathname === '/auth/callback';
    const handlePKCEExchange = async () => {
      if (hasPKCECode && !isAuthCallbackRoute) {
        console.log('[Auth] Exchanging PKCE code for session...');
        try {
          const { data, error } = await supabase.auth.exchangeCodeForSession(authCode!);
          if (error) {
            console.error('[Auth] PKCE exchange error:', error);
          } else {
            console.log('[Auth] PKCE exchange successful');
            cleanAuthUrl();
          }
        } catch (err) {
          console.error('[Auth] PKCE exchange exception:', err);
        }
      }
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[Auth] State change:', event);
        
        if (!isMounted) return;

        // Only synchronous state updates here - no async operations!
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer Supabase calls with setTimeout to prevent deadlock
          setTimeout(() => {
            if (isMounted) fetchUserProfile(session.user.id);
          }, 0);
        } else {
          setUserProfile(null);
          setIsTrainer(false);
          setIsClient(false);
        }

        // Apply any pending CrossFit Jozi QR intents (20%-off promo / September
        // Challenge opt-in) the moment a session exists — regardless of HOW they
        // got here (password login, Google OAuth, magic link). This is the one
        // choke point every sign-in path passes through, which is the point:
        // the narrower consumption in ScanSetup/PodSignup only ever covers a
        // brand-new signup, so an existing member scanning the QR and then
        // logging in through any other route was silently missing both.
        if (event === 'SIGNED_IN' && session?.user) {
          const userId = session.user.id;
          if (getPodPromoIntent()) {
            clearPodPromoIntent();
            supabase.functions
              .invoke('join-gym-promo-signup', { body: { userId } })
              .then(({ error }) => {
                if (error) console.error('[Auth] Promo intent apply error:', error);
              });
          }
          if (getPodChallengeIntent()) {
            clearPodChallengeIntent();
            supabase.functions
              .invoke('join-gym-challenge-signup', { body: { userId } })
              .then(({ error }) => {
                if (error) console.error('[Auth] Challenge intent apply error:', error);
              });
          }
        }

        // Do NOT set loading false here — initial load handles it after awaiting roles
        
        // Clear timeout since we got an auth event
        if (authTimeout) {
          clearTimeout(authTimeout);
          authTimeout = null;
        }
        
        // Clear URL params after successful auth
        if (event === 'SIGNED_IN' && hasAuthToken) {
          cleanAuthUrl();

          // /auth/callback owns all post-OAuth routing — AuthCallback.tsx handles it
          if (window.location.pathname === '/auth/callback') return;

          // Don't compete with ClientOnboarding's dedicated redirect handler
          if (window.location.pathname === '/client-onboarding') return;

          // Skip redirect consumption during password-recovery flows
          const isRecoveryFlow = window.location.pathname === '/reset-password';
          if (isRecoveryFlow) {
            console.log('[Auth] Recovery flow detected, skipping postAuthRedirect');
            // Clear stale redirects so they don't fire later
            sessionStorage.removeItem('postAuthRedirect');
            localStorage.removeItem('postAuthRedirect');
            return;
          }
          
          // Capture then IMMEDIATELY clear — one-shot consumption prevents stale re-use
          const rawRedirect = sessionStorage.getItem('postAuthRedirect') || localStorage.getItem('postAuthRedirect');
          sessionStorage.removeItem('postAuthRedirect');
          localStorage.removeItem('postAuthRedirect');
          sessionStorage.removeItem('pendingEmailVerification');
          localStorage.removeItem('pendingEmailVerification');

          // Never redirect an authenticated user back to a sign-up or onboarding page
          const signupPaths = ['/client-onboarding', '/onboarding', '/pod/signup', '/login'];
          const postAuthRedirect = rawRedirect && !signupPaths.some(p => rawRedirect.startsWith(p))
            ? rawRedirect
            : null;

          if (postAuthRedirect && session?.user) {
            // Guard: never redirect a trainer-only user to a client-only path
            const clientOnlyPrefixes = [
              '/scans', '/scan-ai-report', '/scan-full-report', '/scan-profile',
              '/client-onboarding', '/onboarding', '/questionnaire',
            ];
            const isClientOnlyPath = clientOnlyPrefixes.some((p) => postAuthRedirect.startsWith(p));

            if (isClientOnlyPath) {
              // Fetch roles before deciding — async inside setTimeout is fine here
              setTimeout(async () => {
                const { data: roles } = await supabase.rpc('get_user_roles', { _user_id: session.user.id });
                // Generated RPC type is stale (doesn't know about is_doctor /
                // is_nutritionist yet); any provider type counts as trainer-only here.
                const rolesRow = roles?.[0] as
                  | { is_trainer?: boolean; is_client?: boolean; is_doctor?: boolean; is_nutritionist?: boolean }
                  | undefined;
                const isTrainerOnly = (rolesRow?.is_trainer || rolesRow?.is_doctor || rolesRow?.is_nutritionist) && !rolesRow?.is_client;
                if (isTrainerOnly) {
                  console.warn('[Auth] postAuthRedirect blocked for trainer-only user:', postAuthRedirect);
                  window.location.href = '/trainer-profile';
                } else {
                  sessionStorage.setItem('showWelcomeToast', 'true');
                  window.location.href = postAuthRedirect;
                }
              }, 0);
              return;
            }

            console.log('[Auth] postAuthRedirect found, redirecting to:', postAuthRedirect);
            sessionStorage.setItem('showWelcomeToast', 'true');
            window.location.href = postAuthRedirect;
            return;
          }
        }
      }
    );

    // Initial session check — await roles before resolving loading
    const initSession = async () => {
      try {
        if (hasPKCECode) {
          await handlePKCEExchange();
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          await fetchUserProfile(session.user.id);
        }
      } catch (err) {
        console.error('[Auth] Init session error:', err);
      } finally {
        if (isMounted && !hasAuthToken) {
          setLoading(false);
        }
        // For auth token flows, loading is resolved when onAuthStateChange fires
        // and the safety timeout covers edge cases
        if (isMounted && hasAuthToken) {
          setLoading(false);
        }
      }
    };

    initSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (authTimeout) clearTimeout(authTimeout);
    };
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      const [profileResult, rolesResult] = await Promise.all([
        supabase.from('profiles').select('*').eq('user_id', userId).single(),
        supabase.rpc('get_user_roles', { _user_id: userId })
      ]);

      if (profileResult.data) setUserProfile(profileResult.data);
      setStoredView(getStoredActiveView(userId));
      // Generated RPC type is stale (doesn't know about is_doctor /
      // is_nutritionist yet); isTrainer here really means "is any provider
      // type", which is how every other consumer of this context already
      // treats it.
      const rolesRow = rolesResult.data?.[0] as
        | { is_trainer?: boolean; is_client?: boolean; is_doctor?: boolean; is_nutritionist?: boolean }
        | undefined;
      setIsTrainer(!!(rolesRow?.is_trainer || rolesRow?.is_doctor || rolesRow?.is_nutritionist));
      setIsClient(rolesRow?.is_client || false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setIsTrainer(false);
      setIsClient(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });

    if (error) {
      return { data, error };
    }

    // Auto-login after signup (since email confirmation is disabled)
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      console.error('Auto sign-in after signup failed:', signInError);
      return { data, error: signInError };
    }

    console.log('✅ User signed up and auto-logged in:', signInData.user?.id);
    
    return { data: signInData, error: null };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    
    // Clear all auth-related storage flags to prevent cross-session contamination
    localStorage.removeItem('pendingEmailVerification');
    localStorage.removeItem('postAuthRedirect');
    sessionStorage.removeItem('pendingEmailVerification');
    sessionStorage.removeItem('postAuthRedirect');
    sessionStorage.removeItem('showWelcomeToast');
    
    setUserProfile(null);
    setIsTrainer(false);
    setIsClient(false);
    return { error };
  };

  // Single-role users have no choice to make; only someone who is both gets
  // their stored pick, defaulting to the member view.
  const activeView: ActiveView =
    isTrainer && isClient ? storedView : isTrainer ? 'coach' : 'member';

  const setActiveView = (view: ActiveView) => {
    if (!user || !(isTrainer && isClient)) return;
    storeActiveView(user.id, view);
    setStoredView(view);
  };

  const refreshRoles = async () => {
    if (user) await fetchUserProfile(user.id);
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    userProfile,
    signIn,
    signUp,
    signOut,
    isTrainer,
    isClient,
    activeView,
    setActiveView,
    refreshRoles,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};